// ============================================
// Background Gradient
// Three.js gradient background for all stages
// ============================================

import type { Texture, Mesh, MeshBasicMaterial } from './types';
import { CONFIG } from '../../../config/game-config';
import { getP5 } from '../../../core/p5-instance';
import { addToScene, removeFromScene } from './scene';
import {
    getIsStageTransitioning,
    getPrevStageIndex,
    getCurrentStageIndex
} from '../../../store/index';

// Background objects
let threeBgTexture: Texture | null = null;
let threeBgMesh: Mesh | null = null;

// Cache for optimization
let lastTopHex: string | null = null;
let lastBottomHex: string | null = null;
let lastBgName: string | null = null;
let lastBgProgress: number | null = null;

/**
 * Get the background mesh
 */
export function getThreeBgMesh(): Mesh | null {
    return threeBgMesh;
}

/**
 * Initialize background gradient
 */
export function initThreeBackground(): void {
    const p = getP5();

    // Create canvas for gradient texture
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        console.error('Failed to get 2D context for background gradient');
        return;
    }

    // Initial background using first stage settings
    const stageCfg = CONFIG.stages.backgrounds[0];
    const topHex = colorToHex(p.color(stageCfg.skyTop[0], stageCfg.skyTop[1], stageCfg.skyTop[2]));
    const bottomHex = colorToHex(p.color(stageCfg.skyBottom[0], stageCfg.skyBottom[1], stageCfg.skyBottom[2]));

    const gradient = ctx.createLinearGradient(0, 0, 0, 256);
    gradient.addColorStop(0, topHex);
    gradient.addColorStop(1, bottomHex);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 2, 256);

    threeBgTexture = new THREE.CanvasTexture(canvas);

    // Background plane covers full canvas width
    const bgGeometry = new THREE.PlaneGeometry(CONFIG.layout.canvasWidth, CONFIG.height);
    const bgMaterial = new THREE.MeshBasicMaterial({
        map: threeBgTexture,
        depthWrite: false,
        depthTest: false
    });
    threeBgMesh = new THREE.Mesh(bgGeometry, bgMaterial);
    threeBgMesh.position.z = -500;
    threeBgMesh.visible = true;

    addToScene(threeBgMesh);
}

// p5.Color type alias for cleaner signatures
type P5Color = ReturnType<ReturnType<typeof getP5>['color']>;

/**
 * Update background texture colors
 */
export function updateThreeBgTexture(topCol: P5Color, bottomCol: P5Color): void {
    if (!threeBgTexture || !threeBgTexture.image) {
        return;
    }

    const topHex = colorToHex(topCol);
    const bottomHex = colorToHex(bottomCol);

    // Skip if colors unchanged (performance optimization)
    if (topHex === lastTopHex && bottomHex === lastBottomHex) {
        return;
    }

    lastTopHex = topHex;
    lastBottomHex = bottomHex;

    const canvas = threeBgTexture.image as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return;
    }

    const gradient = ctx.createLinearGradient(0, 0, 0, 256);
    gradient.addColorStop(0, topHex);
    gradient.addColorStop(1, bottomHex);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 2, 256);

    threeBgTexture.needsUpdate = true;
}

/**
 * Update background colors based on current stage
 */
export function updateThreeBackgroundColors(stageCfg: typeof CONFIG.stages.backgrounds[0], transitionProgress: number): void {
    const p = getP5();

    // Get current state
    const isStageTransitioning = getIsStageTransitioning();
    const prevStageIndex = getPrevStageIndex();
    const currentStageIndex = getCurrentStageIndex();

    // Skip if unchanged (performance optimization)
    const isTransitioningNow = isStageTransitioning && (prevStageIndex !== currentStageIndex);
    if (!isTransitioningNow && stageCfg.name === lastBgName && transitionProgress === lastBgProgress) {
        return;
    }
    lastBgName = stageCfg.name;
    lastBgProgress = transitionProgress;

    let topCol = p.color(stageCfg.skyTop[0], stageCfg.skyTop[1], stageCfg.skyTop[2]);
    let bottomCol = p.color(stageCfg.skyBottom[0], stageCfg.skyBottom[1], stageCfg.skyBottom[2]);

    if (isTransitioningNow) {
        const prevCfg = CONFIG.stages.backgrounds[prevStageIndex];
        const prevTop = p.color(prevCfg.skyTop[0], prevCfg.skyTop[1], prevCfg.skyTop[2]);
        const prevBottom = p.color(prevCfg.skyBottom[0], prevCfg.skyBottom[1], prevCfg.skyBottom[2]);
        topCol = p.lerpColor(prevTop, topCol, transitionProgress);
        bottomCol = p.lerpColor(prevBottom, bottomCol, transitionProgress);
    }

    updateThreeBgTexture(topCol, bottomCol);
}

/**
 * Update background mesh position (for shake effects)
 */
export function updateBackgroundPosition(shakeX: number, shakeY: number): void {
    if (threeBgMesh) {
        threeBgMesh.visible = true;
        threeBgMesh.position.x = shakeX;
        threeBgMesh.position.y = -shakeY;
    }
}

/**
 * Dispose of background resources
 */
export function disposeThreeBackground(): void {
    if (threeBgMesh) {
        removeFromScene(threeBgMesh);

        if (threeBgMesh.geometry) {
            threeBgMesh.geometry.dispose();
        }
        if (threeBgMesh.material) {
            (threeBgMesh.material as MeshBasicMaterial).dispose();
        }
        threeBgMesh = null;
    }

    if (threeBgTexture) {
        threeBgTexture.dispose();
        threeBgTexture = null;
    }

    // Reset cache
    lastTopHex = null;
    lastBottomHex = null;
    lastBgName = null;
    lastBgProgress = null;
}

/**
 * Convert p5 color to hex string
 */
function colorToHex(c: P5Color): string {
    const p = getP5();
    const r = p.floor(p.red(c));
    const g = p.floor(p.green(c));
    const b = p.floor(p.blue(c));
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
